// Centralized date/time handling for the app.
//
// Court date/times are the firm's standard and must always be treated as
// New York time (America/New_York), regardless of where the viewing or
// data-entry user is physically located. Values are stored in the database
// as UTC ISO-8601 strings (the absolute instant), and converted to/from
// New York wall-clock time only at the edges (form input + display).

export const NY_TZ = 'America/New_York';

// Returns the offset, in milliseconds, that must be ADDED to a UTC instant to
// get the wall-clock time in `timeZone` at that instant (handles DST).
function tzOffsetMs(timeZone, date) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUTC - date.getTime();
}

// Detects whether a string already carries timezone info (a 'Z' suffix or a
// numeric +hh:mm / -hh:mm offset), i.e. an unambiguous absolute instant.
function hasTimezone(value) {
  return /[zZ]$/.test(value) || /[+-]\d{2}:?\d{2}$/.test(value);
}

// Converts a naive New York wall-clock string ("YYYY-MM-DDTHH:mm",
// "YYYY-MM-DD HH:mm", or "YYYY-MM-DD") into a UTC ISO-8601 string.
export function nyWallTimeToISO(value) {
  if (!value) return null;
  const cleaned = String(value).trim().replace(' ', 'T');
  const [datePart, timePart = '00:00'] = cleaned.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return null;

  const naiveAsUTC = Date.UTC(y, mo - 1, d, h, mi);
  // Two-pass resolution so DST transitions are handled correctly.
  let utc = naiveAsUTC - tzOffsetMs(NY_TZ, new Date(naiveAsUTC));
  utc = naiveAsUTC - tzOffsetMs(NY_TZ, new Date(utc));
  return new Date(utc).toISOString();
}

// Normalizes any incoming court date/time value to a stored UTC ISO string.
// - Values that already include a timezone are parsed as-is (idempotent).
// - Naive values are interpreted as New York wall-clock time.
export function toCourtISO(value) {
  if (!value) return null;
  const str = String(value).trim();
  if (hasTimezone(str)) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return nyWallTimeToISO(str);
}

// Converts a stored UTC ISO instant into the "YYYY-MM-DDTHH:mm" string that an
// <input type="datetime-local"> expects, expressed in New York wall time.
export function isoToNYDateTimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NY_TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
    .formatToParts(d)
    .reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

// "YYYY-MM-DD" key for a stored instant, in New York time (for calendar grouping).
export function nyDateKey(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: NY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(d)
    .reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

// "YYYY-MM-DD" key for today, in New York time.
export function nyTodayKey() {
  return nyDateKey(new Date().toISOString());
}

// Display: full date + time in New York time, e.g. "6/2/2026, 2:30:00 PM".
export function formatNYDateTime(iso, options) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-US', { timeZone: NY_TZ, ...(options || {}) });
}

// Display: date only, in New York time.
export function formatNYDate(iso, options) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { timeZone: NY_TZ, ...(options || {}) });
}

// Display: time only, in New York time, e.g. "02:30 PM".
export function formatNYTime(iso, options) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString('en-US', {
    timeZone: NY_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...(options || {}),
  });
}
